import Cookies from "js-cookie";

const MERCURE_TOKEN_STORAGE_KEY = 'mercureToken';
const MERCURE_PUBLIC_URL_STORAGE_KEY = 'mercurePublicUrl';
const DEFAULT_MERCURE_PATH = '/.well-known/mercure';
const MERCURE_PUBLIC_URL_FROM_ENV = process.env.REACT_APP_MERCURE_PUBLIC_URL;

const normalizeMercurePublicUrl = (value) => {
  if (!value) {
    return value;
  }
  try {
    const parsed = new URL(value);
    if (!parsed.pathname || parsed.pathname === '/') {
      parsed.pathname = DEFAULT_MERCURE_PATH;
      return parsed.toString();
    }
    return parsed.toString();
  } catch (error) {
    return value;
  }
};

const getMercureRegistry = () => {
  if (!window.__mercureEventSources) {
    window.__mercureEventSources = new Set();
  }
  return window.__mercureEventSources;
};

export const setMercureCredentials = (mercure = {}) => {
  const publicUrl = MERCURE_PUBLIC_URL_FROM_ENV || mercure?.public_url;
  if (mercure?.token && publicUrl) {
    const normalizedPublicUrl = normalizeMercurePublicUrl(publicUrl);
    localStorage.setItem(MERCURE_TOKEN_STORAGE_KEY, mercure.token);
    localStorage.setItem(MERCURE_PUBLIC_URL_STORAGE_KEY, normalizedPublicUrl);
    Cookies.set(MERCURE_TOKEN_STORAGE_KEY, mercure.token, {
      expires: 7,
      secure: JSON.parse(process.env.REACT_APP_APP_COOKIE_SECURE.toLowerCase()),
      sameSite: 'Strict',
      path: '/',
      domain: process.env.REACT_APP_APP_COOKIE_DOMAIN
    });
    Cookies.set(MERCURE_PUBLIC_URL_STORAGE_KEY, normalizedPublicUrl, {
      expires: 7,
      secure: JSON.parse(process.env.REACT_APP_APP_COOKIE_SECURE.toLowerCase()),
      sameSite: 'Strict',
      path: '/',
      domain: process.env.REACT_APP_APP_COOKIE_DOMAIN
    });
    return;
  }
  localStorage.removeItem(MERCURE_TOKEN_STORAGE_KEY);
  localStorage.removeItem(MERCURE_PUBLIC_URL_STORAGE_KEY);
  Cookies.remove(MERCURE_TOKEN_STORAGE_KEY, {
    path: '/',
    domain: process.env.REACT_APP_APP_COOKIE_DOMAIN
  });
  Cookies.remove(MERCURE_PUBLIC_URL_STORAGE_KEY, {
    path: '/',
    domain: process.env.REACT_APP_APP_COOKIE_DOMAIN
  });
};

export const getMercureCredentials = () => ({
  token: localStorage.getItem(MERCURE_TOKEN_STORAGE_KEY) || Cookies.get(MERCURE_TOKEN_STORAGE_KEY),
  publicUrl: normalizeMercurePublicUrl(localStorage.getItem(MERCURE_PUBLIC_URL_STORAGE_KEY) || Cookies.get(MERCURE_PUBLIC_URL_STORAGE_KEY)),
});

export const closeMercureSubscription = (eventSource) => {
  if (!eventSource) {
    return;
  }
  eventSource.close();
  getMercureRegistry().delete(eventSource);
};

export const closeAllMercureSubscriptions = () => {
  const registry = getMercureRegistry();
  registry.forEach((eventSource) => {
    eventSource.close();
  });
  registry.clear();
};

export const clearMercureCredentials = () => {
  closeAllMercureSubscriptions();
  localStorage.removeItem(MERCURE_TOKEN_STORAGE_KEY);
  localStorage.removeItem(MERCURE_PUBLIC_URL_STORAGE_KEY);
  Cookies.remove(MERCURE_TOKEN_STORAGE_KEY, {
    path: '/',
    domain: process.env.REACT_APP_APP_COOKIE_DOMAIN
  });
  Cookies.remove(MERCURE_PUBLIC_URL_STORAGE_KEY, {
    path: '/',
    domain: process.env.REACT_APP_APP_COOKIE_DOMAIN
  });
};

export const openMercureSubscription = ({topics = [], onMessage, onError}) => {
  const {token, publicUrl} = getMercureCredentials();
  if (!publicUrl || !token) {
    console.log('[Mercure] missing credentials', {
      hasPublicUrl: Boolean(publicUrl),
      hasToken: Boolean(token),
      topics,
    });
    return null;
  }

  const url = new URL(publicUrl);
  topics.forEach((topic) => {
    url.searchParams.append('topic', topic);
  });
  url.searchParams.append('authorization', token);

  const eventSource = new EventSource(url.toString());
  eventSource.onopen = () => {
    console.log('[Mercure] open', {
      endpoint: publicUrl,
      topics,
      readyState: eventSource.readyState,
    });
  };

  eventSource.onerror = (event) => {
    console.log('[Mercure] error', {
      endpoint: publicUrl,
      topics,
      readyState: eventSource.readyState,
      event,
    });
    if (onError) {
      onError(event);
    }
  };

  const handleMessage = (event) => {
    console.log('[Mercure] raw message', {
      endpoint: publicUrl,
      topics,
      data: event?.data,
    });
    if (onMessage) {
      onMessage(event);
    }
  };

  eventSource.onmessage = handleMessage;

  getMercureRegistry().add(eventSource);
  return eventSource;
};
